import { memo, useCallback, useEffect, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { useGetContactsByPartnerQuery } from 'clickNgo/entities/contact/api'
import { fetchPartnerRequestById } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ContactCreator } from 'clickNgo/features/contact/contactCreator'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'

import { ContactDeactivationStep } from '../../../configs'
import { useBackup, useContactData, useReassignment } from '../../../contexts'
import { ContactDeactivationStepProps } from '../../../models'
import { contactDeactivationModalTranslations } from '../../../translation'
import { ContactDeactivationFooter } from '../../contactDeactivationFooter'
import { ContactDeactivationHeader } from '../../contactDeactivationHeader'

import styles from './BackupContactStep.module.scss'

export const BackupContactStep = memo<ContactDeactivationStepProps>(({ onCancel, onProceed }) => {
  const [isCreateContactVisible, setIsCreateContactVisible] = useState(false)
  const [isSkipConfirmationVisible, setIsSkipConfirmationVisible] = useState(false)
  const [isLoadingPartner, setIsLoadingPartner] = useState(false)
  const [partner, setPartner] = useState<IPartner | null>(null)

  const { contact } = useContactData()
  const { backupedContact, setBackupedContact } = useBackup()
  const { flagsToReassign, rolesToReassign, setAvailableContacts } = useReassignment()

  const { refetch: refetchContacts } = useGetContactsByPartnerQuery(
    {
      topPartnerId: partner?.topPartnerId,
    },
    {
      skip: !partner?.topPartnerId,
    }
  )

  const handleSkipConfirmationClose = useCallback(() => {
    setIsSkipConfirmationVisible(false)
  }, [])

  const handleSkipConfirmationProceed = useCallback(() => {
    setIsSkipConfirmationVisible(false)
    onProceed(ContactDeactivationStep.Completion)
  }, [onProceed])

  const handleProceedClick = useCallback(() => {
    if (backupedContact !== null) {
      if (!backupedContact.contactId.startsWith('new-')) {
        setAvailableContacts([backupedContact])
        if (rolesToReassign.length > 0) {
          return onProceed(ContactDeactivationStep.ReassignRoles)
        }
        if (flagsToReassign.length > 0) {
          return onProceed(ContactDeactivationStep.ReassignFlags)
        }
      }
      return onProceed(ContactDeactivationStep.Completion)
    } else {
      setIsSkipConfirmationVisible(true)
    }
  }, [
    backupedContact,
    flagsToReassign.length,
    onProceed,
    rolesToReassign.length,
    setAvailableContacts,
  ])

  const handleCreateContactClick = useCallback(() => {
    setIsCreateContactVisible(true)
  }, [])

  const handleContactCreatorClose = useCallback(() => {
    setIsCreateContactVisible(false)
  }, [])

  const handleContactCreatorCreate = useCallback(
    (newContact: IContact) => {
      setIsCreateContactVisible(false)
      setBackupedContact(newContact)
      refetchContacts()
    },
    [refetchContacts, setBackupedContact]
  )

  const renderSkipConfirmationContent = useCallback(() => {
    return (
      <>
        <ContactDeactivationHeader
          title={contactDeactivationModalTranslations.skipBackupConfirm.title}
          description={contactDeactivationModalTranslations.skipBackupConfirm.description}
        />
        <div className={styles.confirmButtons}>
          <Button onClick={handleSkipConfirmationClose} color="danger" variant="text">
            <Translation
              text={contactDeactivationModalTranslations.skipBackupConfirm.buttons.cancel}
              span
            />
          </Button>
          <Button onClick={handleSkipConfirmationProceed}>
            <Translation
              text={contactDeactivationModalTranslations.skipBackupConfirm.buttons.confirm}
              span
            />
          </Button>
        </div>
      </>
    )
  }, [handleSkipConfirmationClose, handleSkipConfirmationProceed])

  useEffect(() => {
    if (contact?.partnerId) {
      setIsLoadingPartner(true)
      fetchPartnerRequestById(contact.partnerId, { cacheKey: contact.partnerId })
        .then((result) => {
          setPartner(result)
        })
        .finally(() => {
          setIsLoadingPartner(false)
        })
    }
  }, [contact?.partnerId])

  return (
    <div className={styles.container}>
      <ContactDeactivationHeader
        title={contactDeactivationModalTranslations.backupStep.title}
        description={contactDeactivationModalTranslations.backupStep.description}
        descriptionParams={[{ key: '{email}', value: contact.email }]}
      />
      <LoaderOverlay isVisible={isLoadingPartner} message="Loading partner data..." />
      <RenderWhen condition={partner === null}>
        <div style={{ marginBottom: '2rem' }}>
          <InfoBlock
            text={contactDeactivationModalTranslations.backupStep.partnerNotFound}
            rounded={InfoBlockRounded.Small}
            type={InfoBlockType.Danger}
          />
        </div>
      </RenderWhen>
      <RenderWhen condition={partner !== null}>
        <div className={styles.content}>
          <RenderWhen condition={backupedContact === null}>
            <div className={styles.action}>
              <p>
                <Translation text={contactDeactivationModalTranslations.backupStep.content} span />
              </p>
              <div>
                <Button color="success" onClick={handleCreateContactClick}>
                  <Translation
                    text={contactDeactivationModalTranslations.backupStep.buttons.create}
                    span
                  />
                </Button>
              </div>
            </div>
          </RenderWhen>
          <RenderWhen condition={backupedContact !== null}>
            <div className={styles.data}>
              <p className={styles.label}>
                <Translation
                  text={contactDeactivationModalTranslations.backupStep.newContact}
                  span
                />
              </p>
              <div className={styles.value}>
                {`${backupedContact?.firstName} ${backupedContact?.lastName}`}
              </div>
              <div className={styles.value}>{backupedContact?.email}</div>
            </div>
          </RenderWhen>
          <ContactCreator
            isVisible={isCreateContactVisible}
            onClose={handleContactCreatorClose}
            onCreate={handleContactCreatorCreate}
            partner={partner!}
            onReactivate={handleContactCreatorCreate}
          />
        </div>
      </RenderWhen>
      <ContactDeactivationFooter onCancel={onCancel} onProceed={handleProceedClick} />
      <ModalDialog
        show={isSkipConfirmationVisible}
        onClose={handleSkipConfirmationClose}
        closeOnOutsideClick={false}
        content={renderSkipConfirmationContent}
        size="sm"
      />
    </div>
  )
})

BackupContactStep.displayName = 'BackupContactStep'
